import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import {
  Sparkles,
  ExternalLink,
  Copy,
  Check,
  Trash2,
  Send,
  Save,
  RefreshCw,
  Image as ImageIcon,
  Loader2,
  Facebook,
} from "lucide-react";
import {
  listCanvaPosts,
  listCanvaSources,
  saveCanvaPost,
  deleteCanvaPost,
  publishCanvaPost,
  markCanvaPostShared,
} from "@/lib/api/canva-studio.functions";
import {
  buildCarouselCaption,
  suggestHashtags,
  type CarouselSlide,
} from "@/lib/canva-studio-helpers";
import { FacebookSharePreview } from "@/components/admin/canva/FacebookSharePreview";
import { CarouselBuilder } from "@/components/admin/canva/CarouselBuilder";
import { CanvaPostAnalytics } from "@/components/admin/canva/CanvaPostAnalytics";
const SITE_ORIGIN = "https://nkadimengdannylcs.com";

export const Route = createFileRoute("/_authenticated/portal/admin/canva-studio")({
  component: CanvaStudioPage,
});

// Canva "start a new design" URL for common Facebook-friendly formats.
// These open the Canva editor at the correct canvas size — the admin then
// designs the post and copies the resulting share/embed link back here.
const CANVA_TEMPLATES: { label: string; url: string; size: string }[] = [
  {
    label: "Facebook Post (940×788)",
    url: "https://www.canva.com/design?create&type=TAdxCbvJhcU",
    size: "940 × 788",
  },
  {
    label: "Facebook Cover (1640×924)",
    url: "https://www.canva.com/design?create&type=TAEEmKmv4ec",
    size: "1640 × 924",
  },
  {
    label: "Square Post (1080×1080)",
    url: "https://www.canva.com/design?create&type=TADZ-3rrHmU",
    size: "1080 × 1080",
  },
  {
    label: "Instagram Story (1080×1920)",
    url: "https://www.canva.com/design?create&type=TAFukYAyf80",
    size: "1080 × 1920",
  },
];

type SourceItem =
  | { kind: "news"; id: string; title: string; slug: string; excerpt: string | null; cover: string | null }
  | { kind: "story"; id: string; title: string; body: string }
  | { kind: "generic"; id: null; title: string; body: string };

function buildCaption(item: SourceItem): string {
  const url =
    item.kind === "news"
      ? `https://${new URL(SITE_ORIGIN).hostname}/news/${(item as any).slug}`
      : item.kind === "story"
        ? `https://${new URL(SITE_ORIGIN).hostname}/news`
        : "";
  const body =
    item.kind === "news" ? item.excerpt ?? "" : (item as any).body ?? "";
  const cta =
    item.kind === "story"
      ? "📌 Read today's full legal tip and share your thoughts."
      : "📌 Read the full story on our site.";
  return [
    `⚖️ ${item.title}`,
    "",
    body.trim().slice(0, 600),
    "",
    cta,
    url ? `🔗 ${url}` : "",
    "",
    "#SouthAfricanLaw #LegalTips #NkadimengDanny",
  ]
    .filter(Boolean)
    .join("\n");
}

function shareUrlFor(kind: string, source_id: string | null, sources: any): string {
  const host = SITE_ORIGIN;
  if (kind === "news" && source_id) {
    const n = sources?.news?.find((x: any) => x.id === source_id);
    if (n?.slug) return `${host}/news/${n.slug}`;
  }
  if (kind === "story") return `${host}/news`;
  return host;
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    draft: "bg-muted text-muted-foreground",
    ready: "bg-amber-50 text-amber-700",
    queued: "bg-sky-50 text-sky-700",
    shared: "bg-emerald-50 text-emerald-700",
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        map[status] ?? "bg-muted"
      }`}
    >
      {status}
    </span>
  );
}

function CanvaStudioPage() {
  const qc = useQueryClient();
  const listFn = useServerFn(listCanvaPosts);
  const sourcesFn = useServerFn(listCanvaSources);
  const saveFn = useServerFn(saveCanvaPost);
  const deleteFn = useServerFn(deleteCanvaPost);
  const publishFn = useServerFn(publishCanvaPost);
  const markSharedFn = useServerFn(markCanvaPostShared);

  const posts = useQuery({
    queryKey: ["canva-studio-posts"],
    queryFn: () => listFn({ data: { limit: 80 } }),
  });
  const sources = useQuery({
    queryKey: ["canva-studio-sources"],
    queryFn: () => sourcesFn(),
  });

  // -------- Composer state --------
  const [editingId, setEditingId] = useState<string | null>(null);
  const [kind, setKind] = useState<"news" | "story" | "generic">("news");
  const [sourceId, setSourceId] = useState<string>("");
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [canvaEditUrl, setCanvaEditUrl] = useState("");
  const [canvaImageUrl, setCanvaImageUrl] = useState("");
  const [facebookPostUrl, setFacebookPostUrl] = useState("");
  const [carouselSlides, setCarouselSlides] = useState<CarouselSlide[]>([]);
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);

  const options = useMemo<SourceItem[]>(() => {
    if (kind === "news")
      return (sources.data?.news ?? []).map((n: any) => ({
        kind: "news",
        id: n.id,
        title: n.title,
        slug: n.slug,
        excerpt: n.excerpt,
        cover: n.cover_image_url,
      }));
    if (kind === "story")
      return (sources.data?.stories ?? []).map((s: any) => ({
        kind: "story",
        id: s.id,
        title: s.question,
        body: [s.story, s.solution].filter(Boolean).join("\n\n"),
      }));
    return [];
  }, [kind, sources.data]);

  function autofillFromSource(id: string) {
    setSourceId(id);
    const s = options.find((o) => (o as any).id === id);
    if (!s) return;
    setTitle(s.title);
    setCaption(buildCaption(s));
    // Prime hashtag suggestions from the source body too.
    const body = (s as any).excerpt ?? (s as any).body ?? "";
    setHashtags(suggestHashtags(s.title, body));
  }

  function resetForm() {
    setEditingId(null);
    setKind("news");
    setSourceId("");
    setTitle("");
    setCaption("");
    setCanvaEditUrl("");
    setCanvaImageUrl("");
    setFacebookPostUrl("");
    setCarouselSlides([]);
    setHashtags([]);
  }

  function loadForEdit(row: any) {
    setEditingId(row.id);
    setKind(row.source_kind);
    setSourceId(row.source_id ?? "");
    setTitle(row.title ?? "");
    setCaption(row.caption ?? "");
    setCanvaEditUrl(row.canva_edit_url ?? "");
    setCanvaImageUrl(row.canva_image_url ?? "");
    setFacebookPostUrl(row.facebook_post_url ?? "");
    setCarouselSlides(
      Array.isArray(row.carousel_slides) ? row.carousel_slides : [],
    );
    setHashtags(Array.isArray(row.hashtags) ? row.hashtags : []);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function appendHashtagsToCaption() {
    const suggested = suggestHashtags(title, caption);
    setHashtags(suggested);
    const withoutOldTags = caption
      .split("\n")
      .filter((line) => !/^#\w/.test(line.trim()))
      .join("\n")
      .trimEnd();
    setCaption(`${withoutOldTags}\n\n${suggested.join(" ")}`);
  }

  const currentShareUrl = useMemo(
    () => shareUrlFor(kind, sourceId || null, sources.data),
    [kind, sourceId, sources.data],
  );

  const save = useMutation({
    mutationFn: (status: "draft" | "ready") =>
      saveFn({
        data: {
          id: editingId ?? undefined,
          source_kind: kind,
          source_id: sourceId || null,
          title,
          caption,
          canva_edit_url: canvaEditUrl || null,
          canva_image_url: canvaImageUrl || null,
          facebook_post_url: facebookPostUrl || null,
          carousel_slides: carouselSlides,
          hashtags,
          status,
        },
      }),
    onSuccess: (row: any) => {
      qc.invalidateQueries({ queryKey: ["canva-studio-posts"] });
      if (row?.id) setEditingId(row.id);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["canva-studio-posts"] });
      if (editingId) resetForm();
    },
  });

  const publish = useMutation({
    mutationFn: (row: any) =>
      publishFn({
        data: {
          id: row.id,
          share_url: shareUrlFor(row.source_kind, row.source_id, sources.data),
        },
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["canva-studio-posts"] }),
  });

  const markShared = useMutation({
    mutationFn: (id: string) => markSharedFn({ data: { id } }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["canva-studio-posts"] }),
  });

  async function copyCaption() {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="container-narrow space-y-8 py-10">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">
            Admin · Social
          </p>
          <h1 className="mt-1 flex items-center gap-2 font-display text-3xl text-navy">
            <Sparkles className="h-6 w-6 text-gold" /> Canva Studio
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Design branded Facebook posts in Canva from any news article or
            daily legal story. Draft the caption here, open Canva in the right
            size, paste the exported image URL back, and publish — the retry
            queue takes care of the actual share.
          </p>
        </div>
        <button
          onClick={resetForm}
          className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium hover:border-navy hover:text-navy"
        >
          <RefreshCw className="h-3.5 w-3.5" /> New post
        </button>
      </header>

      {/* --------- Composer --------- */}
      <section className="grid gap-6 rounded-2xl border border-border bg-card p-5 shadow-soft lg:grid-cols-2">
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-navy">Source type</label>
            <div className="mt-1 inline-flex rounded-full border border-border bg-background p-1 text-xs">
              {(["news", "story", "generic"] as const).map((k) => (
                <button
                  key={k}
                  onClick={() => {
                    setKind(k);
                    setSourceId("");
                  }}
                  className={`rounded-full px-3 py-1 capitalize transition ${
                    kind === k
                      ? "bg-navy text-white"
                      : "text-foreground/70 hover:text-navy"
                  }`}
                >
                  {k === "story" ? "Legal story" : k}
                </button>
              ))}
            </div>
          </div>

          {kind !== "generic" && (
            <div>
              <label className="text-xs font-semibold text-navy">
                Pick a {kind === "news" ? "news article" : "legal story"}
              </label>
              <select
                value={sourceId}
                onChange={(e) => autofillFromSource(e.target.value)}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">— choose —</option>
                {options.map((o: any) => (
                  <option key={o.id} value={o.id}>
                    {o.title.slice(0, 90)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-navy">Post title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Headline shown on the design"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-navy">
                Facebook caption
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={appendHashtagsToCaption}
                  className="inline-flex items-center gap-1 text-[11px] text-navy hover:underline"
                  title="Suggest hashtags from title, article body and your practice areas"
                >
                  <Sparkles className="h-3 w-3" /> Suggest hashtags
                </button>
                <button
                  type="button"
                  onClick={copyCaption}
                  className="inline-flex items-center gap-1 text-[11px] text-navy hover:underline"
                >
                  {copied ? (
                    <>
                      <Check className="h-3 w-3 text-emerald-600" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3" /> Copy caption
                    </>
                  )}
                </button>
              </div>
            </div>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={8}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs leading-relaxed"
            />
            {hashtags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {hashtags.map((h) => (
                  <span
                    key={h}
                    className="rounded-full bg-navy/5 px-2 py-0.5 text-[10px] font-medium text-navy"
                  >
                    {h}
                  </span>
                ))}
              </div>
            )}
          </div>

          <CarouselBuilder
            title={title}
            body={caption}
            shareUrl={currentShareUrl}
            hashtags={hashtags}
            slides={carouselSlides}
            onSlidesChange={setCarouselSlides}
            onCarouselCaption={(c) => setCaption(c)}
          />
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-dashed border-border bg-background p-4">
            <p className="text-xs font-semibold text-navy">
              Step 1 — Open Canva at the right size
            </p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Opens a fresh Canva editor in a new tab. Design the graphic, then
              use <em>Share → Public view link</em> or download and host the
              image, and paste the URLs below.
            </p>
            <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {CANVA_TEMPLATES.map((t) => (
                <li key={t.url}>
                  <a
                    href={t.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-xs font-medium text-navy hover:border-navy"
                  >
                    <span>{t.label}</span>
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <label className="text-xs font-semibold text-navy">
              Step 2 — Canva design link (edit or public view)
            </label>
            <input
              value={canvaEditUrl}
              onChange={(e) => setCanvaEditUrl(e.target.value)}
              placeholder="https://www.canva.com/design/…"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-xs"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-navy">
              Step 3 — Exported image URL (used as Facebook thumbnail)
            </label>
            <input
              value={canvaImageUrl}
              onChange={(e) => setCanvaImageUrl(e.target.value)}
              placeholder="https://…/canva-export.png"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-xs"
            />
            {canvaImageUrl && (
              <div className="mt-3 overflow-hidden rounded-md border border-border bg-background">
                <img
                  src={canvaImageUrl}
                  alt="Canva design preview"
                  className="max-h-64 w-full object-contain"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.opacity = "0.3";
                  }}
                />
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-navy">
              Step 4 — Public Facebook post URL (after you post it)
            </label>
            <input
              value={facebookPostUrl}
              onChange={(e) => setFacebookPostUrl(e.target.value)}
              placeholder="https://www.facebook.com/…/posts/…"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-xs"
            />
            <p className="mt-1 text-[10px] text-muted-foreground">
              Paste the live Facebook post URL so we can track engagement
              against it below.
            </p>
          </div>

          <FacebookSharePreview
            post={{
              title,
              caption,
              canva_image_url: canvaImageUrl || null,
              facebook_post_url: facebookPostUrl || null,
            }}
          />

          {editingId && (
            <CanvaPostAnalytics postId={editingId} />
          )}

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => save.mutate("draft")}
              disabled={!title || save.isPending}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-4 py-2 text-xs font-semibold text-navy hover:border-navy disabled:opacity-50"
            >
              {save.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}{" "}
              Save draft
            </button>
            <button
              onClick={() => save.mutate("ready")}
              disabled={!title || !caption || save.isPending}
              className="inline-flex items-center gap-1 rounded-full bg-gold px-4 py-2 text-xs font-semibold text-navy hover:opacity-90 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" /> Mark ready
            </button>
            {editingId && (
              <button
                onClick={() => remove.mutate(editingId)}
                className="inline-flex items-center gap-1 rounded-full border border-rose-300 px-4 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-50"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            )}
          </div>
          {save.error && (
            <p className="text-xs text-rose-700">
              {(save.error as Error).message}
            </p>
          )}
        </div>
      </section>

      {/* --------- Library --------- */}
      <section>
        <h2 className="font-display text-xl text-navy">Recent posts</h2>
        <p className="text-xs text-muted-foreground">
          {posts.data?.length ?? 0} designs in the studio.
        </p>
        {posts.isLoading ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <ul className="mt-4 grid gap-3 md:grid-cols-2">
            {(posts.data ?? []).map((row: any) => {
              const share = shareUrlFor(
                row.source_kind,
                row.source_id,
                sources.data,
              );
              return (
                <li
                  key={row.id}
                  className="flex gap-3 rounded-xl border border-border bg-card p-3 shadow-soft"
                >
                  <div className="h-24 w-24 shrink-0 overflow-hidden rounded-md bg-muted">
                    {row.canva_image_url ? (
                      <img
                        src={row.canva_image_url}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                        <ImageIcon className="h-6 w-6" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusPill status={row.status} />
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {row.source_kind}
                      </span>
                    </div>
                    <p className="line-clamp-1 text-sm font-semibold text-navy">
                      {row.title}
                    </p>
                    <p className="line-clamp-2 text-[11px] text-muted-foreground">
                      {row.caption}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <button
                        onClick={() => loadForEdit(row)}
                        className="text-[11px] font-semibold text-navy hover:underline"
                      >
                        Edit
                      </button>
                      {row.canva_edit_url && (
                        <a
                          href={row.canva_edit_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] text-navy hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" /> Canva
                        </a>
                      )}
                      {row.status !== "shared" && (
                        <button
                          onClick={() => publish.mutate(row)}
                          disabled={
                            !row.caption ||
                            publish.isPending ||
                            row.status === "queued"
                          }
                          className="inline-flex items-center gap-1 rounded-full bg-navy px-2.5 py-1 text-[10px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
                        >
                          <Send className="h-3 w-3" /> Queue FB share
                        </button>
                      )}
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(share)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => markShared.mutate(row.id)}
                        className="inline-flex items-center gap-1 rounded-full bg-[#1877F2] px-2.5 py-1 text-[10px] font-semibold text-white hover:opacity-90"
                      >
                        <Facebook className="h-3 w-3" /> Share now
                      </a>
                    </div>
                  </div>
                </li>
              );
            })}
            {(posts.data ?? []).length === 0 && (
              <li className="text-sm text-muted-foreground">
                No designs yet — pick a source above to draft your first Canva
                post.
              </li>
            )}
          </ul>
        )}
      </section>
    </div>
  );
}
